import { NetworkException } from "./exceptions";

export type RequestParamsRecord = Record<string, unknown>;

function data2Object(
  data: FormData | RequestParamsRecord
): RequestParamsRecord {
  if (data instanceof FormData) {
    data = Object.fromEntries(data) as RequestParamsRecord;
  }

  return data;
}

export function requestGet(
  url: string,
  data?: FormData | Record<string, string>
): Request {
  const record: Record<string, string> | undefined =
    data instanceof FormData
      ? Object.fromEntries(data.entries().map(([k, v]) => [k, String(v)]))
      : data;

  return new Request(
    url + (record ? "?" + new URLSearchParams(record).toString() : ""),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    }
  );
}

export function requestPost(
  url: string,
  data: FormData | RequestParamsRecord = {}
): Request {
  if (data instanceof FormData) {
    const containFiles: boolean =
      data instanceof FormData &&
      data.values().reduce((acc, value) => acc || value instanceof File, false);

    if (containFiles) {
      return new Request(url, {
        method: "POST",
        body: data
      });
    }
  }

  return new Request(url, {
    method: "POST",
    body: JSON.stringify(data2Object(data)),
    headers: {
      "Content-Type": "application/json"
    }
  });
}

export function requestBodyJson(
  url: string,
  method: string,
  data?: FormData | RequestParamsRecord
): Request {
  return new Request(url, {
    method: method,
    body: data ? JSON.stringify(data2Object(data)) : undefined,
    headers: {
      "Content-Type": "application/json"
    }
  });
}

export function requireResult<T>(data: T | null): T {
  if (data === null) {
    throw new NetworkException("Not found", 404);
  }
  return data;
}

/**
 * Fetch url (/api/...) and return json.
 *   Request: https://developer.mozilla.org/en-US/docs/Web/API/Request/Request
 *   - 404: return null
 */
export function fetchJSON<T>(request: Request): Promise<T | null> {
  const jsonRequest = new Request(request, {
    credentials: "same-origin"
  });

  return fetch(jsonRequest).then((response) => {
    if (response.ok) {
      return response.json() as Promise<T>;
    }

    if (response.status === 404) {
      return null;
    }

    throw new NetworkException(
      `Error code is ${response.status}`,
      response.status
    );
  });
}
